import { useCallback, useState } from "react";
import { withErrorHandling } from "../api/axiosHelper";
import type { ApiError, ApiResponse, JobProgress } from "../api/types";
import { logError } from "../logging";
import { wait } from "../utilities/promiseHelper";
import useSetInterval from "./useSetInterval";

type GetJobProgressFunc = (operationId: string) => Promise<ApiResponse<JobProgress>>;

interface Options {
  updateIntervalMs?: number;
  onJobFinished?: (jobProgress: JobProgress) => void;
  onPollingError?: (error: ApiError) => void;
}

const useJobProgress = (
  getJobProgress: GetJobProgressFunc,
  { updateIntervalMs, onJobFinished, onPollingError }: Options
) => {
  const [operationId, setOperationId] = useState<string>();
  const [jobProgress, setJobProgress] = useState<JobProgress>();

  const startJobProgressCheck = useCallback((operationId: string) => {
    setOperationId(operationId);

    setJobProgress({
      startedAt: new Date().toISOString(),
      finished: false,
      totalProgressItems: 0,
      currentProgressItem: 0,
      isSuccess: false,
      progressPercent: 0,
      properties: {},
    });
  }, []);

  const handleInterval = useCallback(async () => {
    if (!operationId) {
      return;
    }

    const [progress, error] = await withErrorHandling(getJobProgress)(operationId);
    if (error) {
      logError(error, "[useJobProgress] getJobProgress");
      if (error.code && error.code >= 400 && error.code <= 500) {
        setOperationId(undefined);
        setJobProgress(undefined);
        onPollingError?.(error);
      }
      return;
    }

    setJobProgress(progress);

    if (progress.finished) {
      await wait((updateIntervalMs ?? 1000) + 1000);
      setOperationId(undefined);
      setJobProgress(undefined);
      onJobFinished?.(progress);
    }
  }, [getJobProgress, onPollingError, onJobFinished, operationId, updateIntervalMs]);

  useSetInterval(handleInterval, updateIntervalMs ?? 1000, { disabled: !operationId });

  return {
    jobProgress,
    startJobProgressCheck,
  };
};

export default useJobProgress;
